// VÍDEO DE PRESENTE COM FUNDO TRANSPARENTE — o motor (sem React)
//
// Porque é que não é um <video> normal:
//   • O iPhone (Safari) não mostra transparência em WebM, e o formato que ele
//     aceita (HEVC com alfa) só se consegue gerar num Mac.
//   • MP4/H.264 toca em todo o lado, mas não tem canal de transparência.
//
// Solução: o ficheiro é um MP4 normal com o DOBRO da altura.
//
//     ┌──────────────┐
//     │    CORES     │  metade de cima: o presente, já sem fundo
//     ├──────────────┤
//     │    MÁSCARA   │  metade de baixo: branco = presente, preto = vazio
//     └──────────────┘
//
// A máscara foi feita UMA vez, fora da app, por um modelo de recorte (IA), e
// vem gravada no ficheiro. Aqui só se juntam as duas metades na placa gráfica
// (WebGL). Não há nenhum "filtro de cor" a adivinhar o que é fundo — por isso
// as partes pretas e verdes do próprio presente ficam intactas.
//
// A metade de cima vem PRÉ-MULTIPLICADA (cor × alfa), que é exatamente o que um
// vídeo com fundo preto já é. Assim as bordas não ganham aura escura.

const SOMBREADOR_VERTICES = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2((a_pos.x + 1.0) * 0.5, (1.0 - a_pos.y) * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const SOMBREADOR_PIXEIS = `
precision mediump float;
uniform sampler2D u_video;
uniform float u_borda;
varying vec2 v_uv;
void main() {
  vec3 cor = texture2D(u_video, vec2(v_uv.x, v_uv.y * 0.5)).rgb;
  float alfa = texture2D(u_video, vec2(v_uv.x, 0.5 + v_uv.y * 0.5)).r;
  // O H.264 nunca devolve 0 e 1 perfeitos: limpa o ruído das pontas.
  alfa = clamp((alfa - 0.03) / 0.94, 0.0, 1.0);
  // Esbatimento das bordas. Vários presentes fazem zoom até encherem o
  // quadro inteiro (o troféu, as medalhas): nesses segundos a máscara é toda
  // opaca e via-se um quadrado com arestas duras. As bordas desvanecem nos
  // últimos ~7%, por isso o quadro nunca se vê — e um presente que não
  // chega às bordas fica exatamente como estava.
  float b = u_borda;
  float suave = smoothstep(0.0, b, v_uv.x) * smoothstep(0.0, b, 1.0 - v_uv.x)
              * smoothstep(0.0, b, v_uv.y) * smoothstep(0.0, b, 1.0 - v_uv.y);
  // Quadro CHEIO (o presente fez zoom e tapa o quadro todo): um esbatimento
  // fino não chega — os cantos escuros do próprio presente vão até à aresta e
  // o quadrado continua a ver-se. Mede-se quanto da borda da MÁSCARA está
  // opaca (12 pontos) e, quanto mais cheia, mais se aplica uma vinheta
  // arredondada: o presente passa a ser um medalhão de bordas suaves em vez
  // de um quadrado. Num quadro normal a borda está vazia e nada disto entra.
  // Média dos quatro lados, 5 pontos por lado. Um troféu pousado no fundo do
  // quadro tem o topo e os lados vazios (média ~0,2: fica como está); um zoom
  // que enche o quadro tem a borda quase toda opaca (média >0,9), mesmo que
  // uma zona escura do próprio presente abra um buraco num dos lados.
  float cobertura = 0.0;
  for (int i = 0; i < 5; i++) {
    float t = 0.1 + 0.2 * float(i);
    cobertura += texture2D(u_video, vec2(t, 0.5 + 0.02 * 0.5)).r;
    cobertura += texture2D(u_video, vec2(t, 0.5 + 0.98 * 0.5)).r;
    cobertura += texture2D(u_video, vec2(0.02, 0.5 + t * 0.5)).r;
    cobertura += texture2D(u_video, vec2(0.98, 0.5 + t * 0.5)).r;
  }
  cobertura /= 20.0;
  float cheio = smoothstep(0.5, 0.8, cobertura);
  vec2 c = v_uv * 2.0 - 1.0;
  float raio = pow(pow(abs(c.x), 4.0) + pow(abs(c.y), 4.0), 0.25);
  float vinheta = 1.0 - smoothstep(0.70, 1.0, raio);
  alfa *= mix(suave, suave * vinheta, cheio);
  // Pré-multiplicado: a cor nunca pode passar o alfa (evita brilho fantasma).
  gl_FragColor = vec4(min(cor, vec3(alfa)), alfa);
}`;

// Largura do esbatimento das bordas, em fração do quadro (0 = desligado).
export const BORDA_SUAVE = 0.07;

function compilar(gl, tipo, fonte) {
  const s = gl.createShader(tipo);
  gl.shaderSource(s, fonte);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const erro = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error('Sombreador não compilou: ' + erro);
  }
  return s;
}

// Prepara o WebGL num <canvas>. Devolve null se o aparelho não tiver WebGL —
// quem chama decide o que mostrar nesse caso.
export function criarPintor(canvas, { borda = BORDA_SUAVE } = {}) {
  try {
    return montarPintor(canvas, borda);
  } catch (erro) {
    // Contexto perdido, sombreador recusado pela placa gráfica... Nunca deixa
    // o ecrã rebentar por causa de um presente: quem chama mostra o vídeo simples.
    console.warn('[presente] WebGL indisponível:', erro);
    return null;
  }
}

function montarPintor(canvas, borda) {
  const opcoes = { alpha: true, premultipliedAlpha: true, antialias: false, preserveDrawingBuffer: false };
  const gl = canvas.getContext('webgl', opcoes) || canvas.getContext('experimental-webgl', opcoes);
  if (!gl || (typeof gl.isContextLost === 'function' && gl.isContextLost())) return null;

  const programa = gl.createProgram();
  gl.attachShader(programa, compilar(gl, gl.VERTEX_SHADER, SOMBREADOR_VERTICES));
  gl.attachShader(programa, compilar(gl, gl.FRAGMENT_SHADER, SOMBREADOR_PIXEIS));
  gl.linkProgram(programa);
  if (!gl.getProgramParameter(programa, gl.LINK_STATUS)) {
    throw new Error('Programa WebGL não ligou: ' + gl.getProgramInfoLog(programa));
  }
  gl.useProgram(programa);
  gl.uniform1f(gl.getUniformLocation(programa, 'u_borda'), borda);

  // Um retângulo que cobre o canvas todo.
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(programa, 'a_pos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const textura = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, textura);
  // Vídeo raramente é potência de 2: estas três linhas são obrigatórias no WebGL 1.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

  return {
    pintar(video) {
      const largura = video.videoWidth;
      const altura = Math.floor(video.videoHeight / 2);
      if (!largura || !altura) return false;
      if (canvas.width !== largura || canvas.height !== altura) {
        canvas.width = largura;
        canvas.height = altura;
      }
      gl.viewport(0, 0, largura, altura);
      gl.bindTexture(gl.TEXTURE_2D, textura);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return true;
    },
    libertar() {
      try { gl.deleteTexture(textura); } catch { /* ignora */ }
      try { gl.deleteBuffer(buffer); } catch { /* ignora */ }
      try { gl.deleteProgram(programa); } catch { /* ignora */ }
      // Os telemóveis só deixam ter ~16 contextos WebGL vivos, por isso cada
      // presente devolve o seu. Mas SÓ se o canvas já saiu da página: o React
      // em modo de desenvolvimento desmonta e volta a montar no MESMO canvas,
      // e um contexto devolvido não volta — o segundo arranque ficava sem imagem.
      if (!canvas.isConnected) {
        try { gl.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* ignora */ }
      }
    },
  };
}

// Tempo máximo que um presente pode ficar no ecrã, mesmo que o evento "ended"
// nunca chegue (aba em segundo plano, vídeo encravado). Duração do vídeo + folga.
export function tempoLimiteMs(duracaoSegundos, folgaMs = 1500, semDuracaoMs = 15000) {
  const d = Number(duracaoSegundos);
  if (!Number.isFinite(d) || d <= 0) return semDuracaoMs;
  return Math.round(d * 1000) + folgaMs;
}

// Liga tudo: vídeo escondido → canvas transparente. Chama `aoTerminar` UMA vez,
// aconteça o que acontecer (fim normal, erro, tempo limite).
export function tocarPresenteTransparente({ video, canvas, aoTerminar, comSom = true, tempoArranqueMs = 8000 }) {
  let terminado = false;
  let pedidoQuadro = null;
  let usaVideoFrame = false;
  let temporizador = null;
  let temporizadorArranque = null;
  const pintor = criarPintor(canvas);

  const terminar = (motivo) => {
    if (terminado) return;
    terminado = true;
    parar();
    if (typeof aoTerminar === 'function') aoTerminar(motivo);
  };

  const proximoQuadro = () => {
    if (terminado) return;
    if (usaVideoFrame) pedidoQuadro = video.requestVideoFrameCallback(desenhar);
    else pedidoQuadro = requestAnimationFrame(desenhar);
  };

  const desenhar = () => {
    if (terminado) return;
    if (pintor && video.readyState >= 2) pintor.pintar(video);
    proximoQuadro();
  };

  const aoMetadados = () => {
    clearTimeout(temporizador);
    temporizador = setTimeout(() => terminar('tempo-limite'), tempoLimiteMs(video.duration));
  };
  const aoAcabar = () => terminar('fim');
  const aoErro = () => terminar('erro');
  const aoTocar = () => clearTimeout(temporizadorArranque);

  video.addEventListener('loadedmetadata', aoMetadados);
  video.addEventListener('ended', aoAcabar);
  video.addEventListener('error', aoErro);
  video.addEventListener('playing', aoTocar);

  // Se nem começar a tocar (rede lenta, ficheiro em falta), não fica pendurado.
  temporizadorArranque = setTimeout(() => terminar('nao-arrancou'), tempoArranqueMs);
  if (video.readyState >= 1) aoMetadados();

  usaVideoFrame = typeof video.requestVideoFrameCallback === 'function';

  // Som: tenta com som; se o telemóvel recusar (sem toque prévio do
  // utilizador), toca sem som em vez de não tocar de todo.
  const arrancar = async () => {
    video.muted = !comSom;
    try {
      await video.play();
    } catch {
      if (terminado) return;
      video.muted = true;
      try { await video.play(); } catch { terminar('nao-arrancou'); }
    }
  };
  arrancar();
  proximoQuadro();

  function parar() {
    clearTimeout(temporizador);
    clearTimeout(temporizadorArranque);
    if (pedidoQuadro !== null) {
      if (usaVideoFrame && typeof video.cancelVideoFrameCallback === 'function') video.cancelVideoFrameCallback(pedidoQuadro);
      else cancelAnimationFrame(pedidoQuadro);
      pedidoQuadro = null;
    }
    video.removeEventListener('loadedmetadata', aoMetadados);
    video.removeEventListener('ended', aoAcabar);
    video.removeEventListener('error', aoErro);
    video.removeEventListener('playing', aoTocar);
    try { video.pause(); } catch { /* ignora */ }
    if (pintor) pintor.libertar();
  }

  return {
    temWebGL: !!pintor,
    parar: () => {
      if (terminado) return;
      terminado = true;
      parar();
    },
  };
}
